import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TrendingUp, Award, Crown, Download, ShoppingBag } from "lucide-react";

/** Friendly labels for the tracked outbound merch destinations. */
const MERCH_LABELS = {
    dalboocreate: "Angelwear (homepage)",
    dalboocreate_pendants: "Angel pendant necklaces",
    dalboocreate_tees: "Angel t-shirts",
    dalboocreate_streetwear: "Faith-based streetwear",
};

/**
 * "Merch pulse" — ranks the dalboocreate outbound links by member clicks so
 * the curator can see which merch line resonates most. Highlights the
 * most-clicked category (by 30-day clicks, all-time as tiebreaker).
 */
function MerchPulse({ stats }) {
    const ranked = useMemo(() => {
        if (!stats) return [];
        return Object.entries(stats)
            .map(([dest, s]) => ({ dest, label: MERCH_LABELS[dest] || dest, ...s }))
            .sort((a, b) => (b.month_clicks - a.month_clicks) || (b.total - a.total));
    }, [stats]);

    if (!ranked.length) return null;
    const top = ranked[0];
    const anyClicks = ranked.some((r) => r.total > 0);

    return (
        <div className="mb-14" data-testid="admin-merch-pulse">
            <div className="flex items-center gap-2 mb-2">
                <ShoppingBag className="w-4 h-4 text-[#cfa052]" aria-hidden="true" />
                <span className="font-label text-[10px] tracking-[0.3em] uppercase text-[#cfa052]">
                    Merch pulse
                </span>
            </div>
            <p className="font-poetic text-[#9a958a] text-sm mb-5" data-testid="admin-merch-pulse-headline">
                {anyClicks ? (
                    <>
                        Most clicked (30 days):{" "}
                        <span className="text-[#e8e4db]">{top.label}</span>
                    </>
                ) : (
                    "No merch clicks recorded yet — counts will appear as members tap the dalboocreate links."
                )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {ranked.map((row, idx) => {
                    const isTop = idx === 0 && anyClicks;
                    return (
                        <div
                            key={row.dest}
                            className={`rounded border p-5 ${
                                isTop
                                    ? "border-[#cfa052]/40 bg-[#cfa052]/[0.04]"
                                    : "border-white/10 bg-white/[0.02]"
                            }`}
                            data-testid={`admin-merch-pulse-${row.dest}`}
                        >
                            <div className="flex items-center gap-2 mb-3 min-h-[16px]">
                                {isTop && <Crown className="w-3.5 h-3.5 text-[#cfa052]" aria-hidden="true" />}
                                <span className={`font-label text-[9px] tracking-[0.2em] uppercase ${isTop ? "text-[#cfa052]" : "text-[#6e6a62]"}`}>
                                    {isTop ? "Most clicked" : `#${idx + 1}`}
                                </span>
                            </div>
                            <div className="font-poetic text-[#e8e4db] text-[15px] leading-snug">{row.label}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-numerals text-2xl text-[#cfa052]">{row.month_clicks}</span>
                                <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]">clicks · 30 days</span>
                            </div>
                            <div className="mt-1.5 font-poetic text-xs text-[#6e6a62]">
                                {row.week_clicks} this week · {row.total} all-time
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Curator popularity dashboard. Ranks all 60 angels by rolling pick counts
 * (last 7 days, 30 days, all-time) so the curator can see at a glance which
 * designs are resonating with members. Useful for informing what to
 * commission next for dalboocreate merch.
 */
export default function AdminAngelsPopularity() {
    const { admin } = useAdminAuth();
    const [data, setData] = useState(null);
    const [merchStats, setMerchStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [popRes, outboundRes] = await Promise.all([
                    api.get("/admin/angels-popularity"),
                    api.get("/admin/outbound-stats").catch(() => null),
                ]);
                if (!cancelled) {
                    setData(popRes.data);
                    setMerchStats(outboundRes?.data?.stats || null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const top3 = useMemo(() => (data?.items || []).slice(0, 3), [data]);
    const items = data?.items || [];
    const totals = data?.totals || { last7d: 0, last30d: 0, allTime: 0 };

    /**
     * Build a CSV from the ranked items and trigger a browser download.
     * Useful for sharing the leaderboard with the dalboocreate merch team
     * (or anyone else who doesn't have admin access). One row per angel,
     * with a header row that names every column.
     */
    const downloadCsv = () => {
        if (!items.length) return;
        const escape = (v) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const header = ["rank", "number", "name", "last_7d", "last_30d", "all_time", "status"];
        const rows = items.map((row, i) => [
            i + 1,
            row.number,
            row.name || "",
            row.last7d,
            row.last30d,
            row.allTime,
            !row.isSelectable ? "unavailable" : row.hasArtwork ? "live" : "placeholder",
        ]);
        const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n") + "\n";
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const today = new Date().toISOString().slice(0, 10);
        const a = document.createElement("a");
        a.href = url;
        a.download = `angels-popularity-${today}.csv`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    };

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-popularity-page">
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · popularity</span>
                    <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="admin-popularity-title">
                                Angels in <span className="italic">flight</span>
                            </h1>
                            <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                                Which angels are members reaching for. Use this to inform the next
                                wave of merchandise.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={downloadCsv}
                            disabled={!items.length}
                            data-testid="admin-popularity-csv-btn"
                            className="self-start md:self-auto inline-flex items-center gap-2 border border-[#cfa052]/40 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-5 py-2.5 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Download CSV
                        </button>
                    </div>
                </div>

                <AdminSubNav />

                {!loading && <MerchPulse stats={merchStats} />}

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-popularity-loading">
                        counting the wings…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-32" data-testid="admin-popularity-empty">
                        <TrendingUp className="w-8 h-8 mx-auto text-[#6e6a62] mb-3" />
                        <p className="font-poetic text-[#9a958a]">No selections recorded yet.</p>
                    </div>
                ) : (
                    <>
                        {/* Top-3 podium */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14" data-testid="admin-popularity-podium">
                            {top3.map((row, idx) => {
                                const rankLabels = ["Most chosen", "Runner-up", "Third"];
                                const Icon = idx === 0 ? Crown : Award;
                                const iconColor = idx === 0 ? "text-[#cfa052]" : "text-[#9a958a]";
                                return (
                                    <div
                                        key={row.number}
                                        className={`rounded border ${idx === 0 ? "border-[#cfa052]/40 bg-[#cfa052]/[0.04]" : "border-white/10 bg-white/[0.02]"} p-6`}
                                        data-testid={`admin-popularity-podium-${idx + 1}`}
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <Icon className={`w-4 h-4 ${iconColor}`} />
                                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                                {rankLabels[idx]}
                                            </span>
                                        </div>
                                        <div className="font-numerals text-5xl text-[#e8e4db] leading-none">
                                            {row.number}
                                        </div>
                                        <div className="mt-3 font-poetic text-[#9a958a] text-base">
                                            {row.name || "—"}
                                        </div>
                                        <div className="mt-5 flex items-baseline gap-3">
                                            <span className="font-numerals text-2xl text-[#cfa052]">{row.last7d}</span>
                                            <span className="font-label text-[10px] tracking-[0.2em] uppercase text-[#6e6a62]">picks · 7 days</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Totals strip */}
                        <div className="grid grid-cols-3 gap-4 mb-10" data-testid="admin-popularity-totals">
                            <div className="rounded border border-white/10 bg-white/[0.02] p-5">
                                <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">All members · 7 days</div>
                                <div className="font-numerals text-3xl text-[#e8e4db]">{totals.last7d}</div>
                            </div>
                            <div className="rounded border border-white/10 bg-white/[0.02] p-5">
                                <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">All members · 30 days</div>
                                <div className="font-numerals text-3xl text-[#e8e4db]">{totals.last30d}</div>
                            </div>
                            <div className="rounded border border-white/10 bg-white/[0.02] p-5">
                                <div className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-2">All members · all-time</div>
                                <div className="font-numerals text-3xl text-[#cfa052]">{totals.allTime}</div>
                            </div>
                        </div>

                        {/* Full ranked table */}
                        <div className="overflow-x-auto" data-testid="admin-popularity-table-wrap">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Rank</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Angel</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6">Name</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6 text-right">7d</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6 text-right">30d</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3 pr-6 text-right">All-time</th>
                                        <th className="font-label text-[10px] text-[#cfa052] py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5 font-poetic" data-testid="admin-popularity-list">
                                    {items.map((row, i) => (
                                        <tr
                                            key={row.number}
                                            className="text-[#e8e4db]"
                                            data-testid={`admin-popularity-row-${row.number}`}
                                        >
                                            <td className="py-3 pr-6 text-[#6e6a62] font-label text-[10px] tracking-widest">
                                                {String(i + 1).padStart(2, "0")}
                                            </td>
                                            <td className="py-3 pr-6">
                                                <span className="font-numerals text-lg text-[#cfa052]">{row.number}</span>
                                            </td>
                                            <td className="py-3 pr-6 text-[#9a958a]">{row.name || "—"}</td>
                                            <td className="py-3 pr-6 text-right font-numerals text-base">
                                                {row.last7d > 0 ? (
                                                    <span className="text-[#e8e4db]">{row.last7d}</span>
                                                ) : (
                                                    <span className="text-[#6e6a62]">0</span>
                                                )}
                                            </td>
                                            <td className="py-3 pr-6 text-right font-numerals text-base text-[#9a958a]">
                                                {row.last30d || 0}
                                            </td>
                                            <td className="py-3 pr-6 text-right font-numerals text-base text-[#9a958a]">
                                                {row.allTime || 0}
                                            </td>
                                            <td className="py-3 font-label text-[10px] text-[#6e6a62]">
                                                {!row.isSelectable
                                                    ? "unavailable"
                                                    : row.hasArtwork
                                                        ? "live"
                                                        : "placeholder"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </section>
        </PortalLayout>
    );
}
